import { FloatingFormItem } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  InvoiceRecipientType,
  InvoiceSpedStatusRegistered,
  InvoiceSpedStatusRegisteredConstants,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import {
  SpedDocumentInvoiceRecipientType,
  SpedDocumentInvoiceRecipientTypeLabel,
  SpedDocumentInvoiceType,
  SpedDocumentInvoiceTypeColor,
  SpedDocumentStatus,
} from '@/core/domain/sped/sped-documents/sped-documents.types';
import { Row, Col, Form, Input, Select, Table, Button, Modal } from 'antd';
import {
  useSpedDocumentContext,
  useSpedDocumentHasItemContext,
} from '@/pages/FiscalPages/SpedPage/SpedDocumentsPage/SpedDocumentDetailPage';
import {
  useSpedDocumentInvoices,
  useSpedDocumentManageImports,
} from '@/core/domain/sped/sped-documents/sped-documents.hooks';
import { useState, useCallback, useEffect } from 'react';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useDebounce } from '@/hooks';

interface FormValues {
  number: string;
  invoiceTypes: SpedDocumentInvoiceType[];
  recipientType: SpedDocumentInvoiceRecipientType;
}

export default function SpedDocumentInvoices() {
  const spedDocument = useSpedDocumentContext();
  const [form] = Form.useForm<FormValues>();

  const hasRemovePermission = hasPermissions([
    Authorities.SPED_DOCUMENT_IMPORT_INVOICE,
  ]);

  const formValues = useDebounce(Form.useWatch([], form), 1000);

  const statusEnabled = [
    SpedDocumentStatus.AWAITING_SELECTION,
    SpedDocumentStatus.ASSESSING,
    SpedDocumentStatus.REOPENED,
    SpedDocumentStatus.REJECTED,
  ];

  const removeDisabled =
    (!!spedDocument?.status && !statusEnabled.includes(spedDocument?.status)) ||
    !hasRemovePermission;

  const [idsSelected, setIdsSelected] = useState<number[]>([]);

  const { data: invoices, dataUpdatedAt } = useSpedDocumentInvoices({
    ...formValues,
    spedDocumentId: spedDocument?.id as number,
  });

  const [, handleHasItem] = useSpedDocumentHasItemContext();

  const spedDocumentManageImports = useSpedDocumentManageImports();

  useEffect(() => {
    if (
      invoices?.some((item) =>
        [
          InvoiceSpedStatusRegistered.PENDING,
          InvoiceSpedStatusRegistered.ERROR,
        ].includes(item.statusRegisteredInPlugStorage),
      )
    ) {
      handleHasItem(true);
    }
  }, [invoices, dataUpdatedAt]);

  const removeConfirm = useCallback(async () => {
    Modal.confirm({
      title: 'Remoção de notas fiscais importadas',
      content: `Tem certeza que deseja remover as "${idsSelected.length}" seleções de notas fiscais importadas? essa ação não poderá ser revertida`,
      okText: `Remover "${idsSelected.length}"`,
      onOk: async () => {
        await spedDocumentManageImports.mutateAsync({
          importType: 'REMOVE',
          spedDocumentId: spedDocument?.id as number,
          items: idsSelected.map((id) => ({
            entityId: id,
            entityEnum: 'INVOICE',
          })),
        });
        setIdsSelected([]);
      },
      okButtonProps: { danger: true },
    });
  }, [idsSelected]);

  return (
    <>
      <Form form={form}>
        <Row style={{ marginBottom: 24 }} gutter={[16, 16]}>
          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="number" label="N° da nota fiscal">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="recipientType" label="Tipo de destinatário">
              <Select
                allowClear
                style={{ width: '100%' }}
                options={Object.values(InvoiceRecipientType).map((value) => ({
                  value,
                  label: SpedDocumentInvoiceRecipientTypeLabel[value],
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="invoiceTypes" label="Tipo de nota fiscal">
              <Select
                allowClear
                style={{ width: '100%' }}
                mode="tags"
                options={Object.values(InvoiceType)
                  .filter((value) => value !== 'CFE')
                  .map((value) => ({
                    value,
                    label: value,
                  }))}
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <Table
        dataSource={invoices || []}
        rowKey={(item) => item.invoiceId}
        rowSelection={{
          selectedRowKeys: idsSelected,
          onChange: (keys) => setIdsSelected(keys as number[]),
          getCheckboxProps: (props) => {
            const disabled =
              props.statusRegisteredInPlugStorage ===
                InvoiceSpedStatusRegistered.INSERTED || removeDisabled;
            return { disabled };
          },
        }}
        pagination={false}
        columns={[
          { title: 'N° da nota fiscal', dataIndex: 'number' },
          { title: 'Chave da nota fiscal', dataIndex: 'key' },
          {
            title: 'Destinatário',
            render: (_, item) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span>{item.recipientName}</span>
                <span style={{ fontSize: 14 }}>
                  {SpedDocumentInvoiceRecipientTypeLabel[item.recipientType]}
                </span>
              </div>
            ),
          },
          {
            title: 'Tipo',
            render: (_, item) => (
              <StatusTag
                color={SpedDocumentInvoiceTypeColor[item.invoiceType]}
                label={item.invoiceType}
              />
            ),
          },
          {
            title: 'Status',
            render: (_, item) => {
              const info =
                InvoiceSpedStatusRegisteredConstants[
                  item.statusRegisteredInPlugStorage
                ];

              return <StatusTag color={info.color} label={info.label} />;
            },
          },
        ]}
        scroll={{ x: true }}
      />

      <div style={{ marginTop: 24 }}>
        <Button
          type="primary"
          danger
          disabled={idsSelected.length < 1 || removeDisabled}
          onClick={removeConfirm}
        >
          {idsSelected.length > 0
            ? `Remover "${idsSelected.length}" ${
                idsSelected.length > 1 ? 'seleções' : 'seleção'
              }`
            : 'Remover seleção'}
        </Button>
      </div>
    </>
  );
}
